import * as Sentry from '@sentry/react';
import { branch, compose, renderNothing } from 'recompose';

import waitForGraphQL from 'js/lib/waitForGraphQL';

import { getClientName } from 'bundles/cms/utils/SpaceUtils';
import type { Maybe } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type { EntWebsiteBlockAssemblyFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import { withEntWebsiteContext } from 'bundles/ent-website/components/EntWebsiteContext';
import type { PropsFromWithEntWebsiteContext } from 'bundles/ent-website/components/EntWebsiteContext';
import EnterpriseWebsiteExperiments from 'bundles/epic/clients/EnterpriseWebsite';

export type BlockModuleOuterProps = {
  blockId: string;
  preLoadHeroImage?: boolean;
};

export type BlockModuleInnerProps<TContentFragment> = {
  content: TContentFragment;
  assembly: Omit<EntWebsiteBlockAssemblyFragment, 'sys'>;
};

// Internal base type for the "Query result" data type
// e.g. `EntWebsiteHowQuery` from the `__generated__/contentfulTypes`
type TBaseQueryData<TContentFragment> = {
  __typename?: 'Query';
  entWebsiteBlockAssembly?: Maybe<{
    __typename?: 'EntWebsiteBlockAssembly';
    reference?: TContentFragment;
  }>;
};

export function withBlockQuery<TContentFragment, TQueryData extends TBaseQueryData<TContentFragment>>(query: any) {
  return compose<BlockModuleInnerProps<TContentFragment>, BlockModuleOuterProps>(
    withEntWebsiteContext,
    waitForGraphQL<
      BlockModuleOuterProps & PropsFromWithEntWebsiteContext,
      TQueryData,
      { id: string; preview?: boolean; locale: string },
      BlockModuleInnerProps<TContentFragment>
    >(query, {
      options: ({ blockId, entWebsiteContext: { isPreview, locale } }) => ({
        context: { clientName: getClientName('ENTERPRISE', isPreview) },
        variables: { id: blockId, preview: isPreview, locale },
        errorPolicy: 'all',
      }),
      props: ({ data, ownProps }) => {
        const assembly = (data?.entWebsiteBlockAssembly ?? {}) as EntWebsiteBlockAssemblyFragment;
        const content = (data?.entWebsiteBlockAssembly?.reference ?? {}) as TContentFragment;
        if (!content) {
          Sentry.captureException('Content returned from Contentful is empty');
        }

        if (data?.error) {
          Sentry.captureException(data?.error);
        }

        return {
          content,
          assembly,
          preLoadHeroImage: ownProps.preLoadHeroImage,
        };
      },
    }),
    branch(
      //  if locale is not supported it should be strictly false
      ({ assembly, entWebsiteContext }: BlockModuleInnerProps<TContentFragment> & PropsFromWithEntWebsiteContext) =>
        EnterpriseWebsiteExperiments.get('supportedLocalesKillswitch') &&
        assembly?.supportedLocales?.[entWebsiteContext?.locale] === false,
      renderNothing
    )
  );
}

export default withBlockQuery;
